import { useState } from "react";
import { useNotify } from "ra-core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { getSupabaseClient } from "../../providers/supabase/supabase";

/* ------------------------------------------------------------ motores (PRD G.1) */
export type EngineId =
  | "auto"
  | "gemini"
  | "groq"
  | "openrouter"
  | "deepseek"
  | "claude_plan"
  | "claude_api";

// Textos del prototipo (ENGINES)
export const ENGINES: {
  id: EngineId;
  short: string;
  name: string;
  tag: "free" | "paid" | "plan";
  desc: string;
  lim: string;
}[] = [
  {
    id: "auto",
    short: "Automático",
    name: "Automático: el gratis que tenga cupo",
    tag: "free",
    desc: "Usa Gemini; si se acabó su cupo del día pasa a Groq y luego a OpenRouter. Nunca usa uno de pago sin tu permiso.",
    lim: "Suma los cupos gratis de los tres",
  },
  {
    id: "gemini",
    short: "Gemini",
    name: "Google Gemini (Flash)",
    tag: "free",
    desc: "Puede buscar en Google por sí mismo, así que trae noticias, vacantes y redes sin otro servicio. Buen español.",
    lim: "Cupo diario bajo y cambiante: Google no publica la cifra",
  },
  {
    id: "groq",
    short: "Groq",
    name: "Groq (GPT-OSS, Llama)",
    tag: "free",
    desc: "Muy rápido. No navega: el CRM lee el sitio web de la empresa y busca con Tavily, y se lo pasa.",
    lim: "Límites por minuto y por día según el modelo",
  },
  {
    id: "openrouter",
    short: "OpenRouter",
    name: "OpenRouter · modelos gratis (DeepSeek, Qwen, Llama…)",
    tag: "free",
    desc: "Una llave para muchos modelos marcados “free”. No navega: el CRM le pasa el texto. A veces es lento.",
    lim: "20 por minuto; 50 al día sin saldo",
  },
  {
    id: "deepseek",
    short: "DeepSeek",
    name: "DeepSeek (API oficial)",
    tag: "paid",
    desc: "Buena calidad a costo muy bajo, pero no es gratis: pagas por uso. No navega por sí mismo.",
    lim: "Sin cupo diario; centavos por empresa",
  },
  {
    id: "claude_plan",
    short: "Claude (tu plan)",
    name: "Claude con tu plan (vía Cowork)",
    tag: "plan",
    desc: "La mejor investigación y opinión de ventas. No responde al instante: la empresa entra a una cola y Claude la investiga en una tarea programada de Cowork usando tu plan.",
    lim: "Consume tu plan de Claude",
  },
  {
    id: "claude_api",
    short: "Claude API",
    name: "Claude API (Opus 5)",
    tag: "paid",
    desc: "Calidad de Claude al instante desde el botón. Se cobra aparte de tu plan.",
    lim: "Pago por uso (unos $0.15–0.40 USD por empresa)",
  },
];
export const engineShort = (id?: string | null) =>
  ENGINES.find((e) => e.id === id)?.short ?? id ?? "";

export type EngineStatus = {
  service: string;
  has_key: boolean;
  enabled: boolean;
  paid: boolean;
  calls_today: number;
  remaining: number | null;
  limit_n: number | null;
  exhausted_until: string | null;
  queued: number;
  day_limit: number | null; // Gemini: 20 al día
  month_used: number;
  month_limit: number | null; // Tavily, SerpApi, búsqueda de Google de Gemini
  blocked: boolean; // llegó al 90 % del mes: el CRM deja de usarlo
};

export const useEngines = () =>
  useQuery({
    queryKey: ["ai_engines"],
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data, error } =
        await getSupabaseClient().rpc("ai_engines_status");
      if (error) throw error;
      return data as EngineStatus[];
    },
  });

const FREE = ["gemini", "groq", "openrouter"];
/** ¿Se puede usar este motor? (llave guardada; los de pago además activados) */
export const engineReady = (
  id: EngineId,
  st?: EngineStatus[],
): string | null => {
  if (!st) return null;
  const s = (x: string) => st.find((e) => e.service === x);
  if (id === "claude_plan") return null;
  if (id === "auto")
    return FREE.some((x) => s(x)?.has_key)
      ? null
      : "Guarda la llave de Gemini, Groq u OpenRouter";
  const e = s(id);
  if (!e?.has_key) return "Falta su llave en IA y ajustes";
  if (e.paid && !e.enabled) return "Es de pago: actívalo en IA y ajustes";
  return null;
};

export type AiSettings = {
  ai_engine: EngineId;
  ai_fallback: string[];
  nightly_research: boolean;
  nightly_count: number;
  nightly_hour: string;
  calibration: boolean;
  thermo_proposal: {
    pesos: Record<string, number>;
    prob?: Record<string, number>;
    explicacion: string;
    cerradas: number;
    fecha: string;
    motor: string;
  } | null;
};
export const useAiSettings = () =>
  useQuery({
    queryKey: ["crm_settings", "ai"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("crm_settings")
        .select(
          "ai_engine, ai_fallback, nightly_research, nightly_count, nightly_hour, calibration, thermo_proposal",
        )
        .eq("id", 1)
        .single();
      if (error) throw error;
      return data as AiSettings;
    },
  });

/** Selector de motor (ficha y selección masiva). */
export const EngineSelect = ({
  value,
  onChange,
  className,
}: {
  value: EngineId;
  onChange: (v: EngineId) => void;
  className?: string;
}) => {
  const { data: st } = useEngines();
  return (
    <select
      aria-label="Motor de IA"
      value={value}
      onChange={(e) => onChange(e.target.value as EngineId)}
      className={cn(
        "h-8 w-auto max-w-40 rounded-md border bg-card px-2 text-sm text-foreground",
        className,
      )}
    >
      {ENGINES.map((e) => {
        const why = engineReady(e.id, st);
        return (
          <option key={e.id} value={e.id} disabled={!!why}>
            {e.short}
            {why ? " (no disponible)" : ""}
          </option>
        );
      })}
    </select>
  );
};

/* ------------------------------------------------------------ investigación (G.2) */
export type Research = {
  id: number;
  directory_id: number;
  engine: string;
  engine_requested: EngineId;
  status: "en_cola" | "investigando" | "lista" | "error";
  requested_at: string;
  finished_at: string | null;
  dossier: Dossier | null;
  match: Record<string, AiMatch> | null;
  strategy: AiStrategy | null;
  script: Record<string, AiScript> | null;
  sources: { url: string; title?: string; fecha?: string }[] | null;
  error: string | null;
  attempts: number;
  ai_calls: number;
  search_calls: number;
  duration_ms: number | null;
  not_before: string | null;
};
export type Cited = {
  fuente?: string;
  fecha?: string;
  url?: string;
  [k: string]: unknown;
};
export type Person = Cited & {
  nombre: string;
  puesto?: string;
  telefono?: string | null;
  email?: string | null;
  perfil_url?: string | null;
};
export type Dossier = {
  resumen?: Cited & { texto?: string };
  rfc?: string | null;
  datos_clave?: (Cited & { dato: string; valor: string })[];
  presencia_digital?: (Cited & { canal: string; valor: string })[];
  tecnologia?: (Cited & { dato: string })[];
  tecnologia_detectada?: string[];
  senales?: (Cited & { texto: string })[];
  personas?: Person[];
  registros?: (Cited & { registro: string; valor: string })[];
  competidores?: (Cited & { nombre: string; ubicacion?: string })[];
  riesgos?: (Cited & { texto: string })[];
  limitaciones?: string[];
  personas_descartadas?: string;
  lista_69b?: {
    corte: string | null;
    coincidencias: { rfc: string; nombre: string; situacion: string }[];
  };
};
export type AiMatch = {
  pct?: number;
  necesidad?: string;
  evidencia?: string;
  solucion?: string;
  siguiente?: string;
  apertura?: string;
  pitch?: string;
  objeciones?: [string, string][];
};
export type AiStrategy = {
  lider?: string;
  despues?: string[];
  canal?: string;
  horario?: string;
  plan?: string[];
  opinion?: string;
  confianza?: string;
  por_que_confianza?: string;
};
export type AiScript = {
  objetivo?: string;
  con_quien?: string;
  tesis?: string;
  evidencia?: string;
  prueba_social?: string;
  urgencia?: string;
  antes?: string[];
  recepcion?: string;
  apertura?: string;
  motivo?: string;
  spin?: [string, string, string][];
  propuesta?: string;
  objeciones?: [string, string][];
  cierre?: string;
  voz?: string;
  whatsapp?: string;
  correo_asunto?: string;
  correo?: string;
};

export const useResearch = (dirId?: number | null) =>
  useQuery({
    queryKey: ["research", dirId],
    enabled: !!dirId,
    refetchInterval: (q) =>
      ["en_cola", "investigando"].includes(
        (q.state.data as Research | null)?.status ?? "",
      )
        ? 5000
        : false,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("research")
        .select("*")
        .eq("directory_id", dirId!)
        .order("requested_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as Research | null;
    },
  });

/**
 * Limpia lo que escribió la IA: sin textos vacíos (así se usa el texto base), sin
 * referencias a campos internos ("(expediente.x)") ni puntos dobles.
 */
export const cleanAi = (x: unknown): unknown => {
  if (typeof x === "string") {
    const t = x
      .replace(/\s*\((?:ver\s+)?(?:expediente|packet|paquete)[^)]*\)/gi, "")
      .replace(/\.{2,}$/, ".")
      .trim();
    return t && t !== "." ? t : undefined;
  }
  if (Array.isArray(x)) return x.map(cleanAi).filter((v) => v !== undefined);
  if (x && typeof x === "object")
    return Object.fromEntries(
      Object.entries(x).map(([k, v]) => [k, cleanAi(v)]),
    );
  return x;
};

/** La investigación lista más reciente (aunque haya otra en cola). */
export const useReadyResearch = (dirId?: number | null) =>
  useQuery({
    queryKey: ["research", dirId, "lista"],
    enabled: !!dirId,
    queryFn: async () => {
      const { data } = await getSupabaseClient()
        .from("research")
        .select("*")
        .eq("directory_id", dirId!)
        .eq("status", "lista")
        .order("finished_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return data
        ? ({
            ...data,
            dossier: cleanAi(data.dossier),
            match: cleanAi(data.match),
            strategy: cleanAi(data.strategy),
            script: cleanAi(data.script),
          } as Research)
        : null;
    },
  });

export const fecha = (s?: string | null) =>
  s
    ? new Date(s).toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "—";

export const personalizedLabel = (r: Research) =>
  `Personalizada con la investigación de ${r.engine === "claude_plan" ? "Claude (tu plan)" : r.engine} el ${fecha(r.finished_at)}`;

export const statusLabel = (r?: Research | null) =>
  !r
    ? null
    : r.status === "en_cola"
      ? r.engine_requested === "claude_plan"
        ? "En cola: la investiga Claude con tu plan en la próxima tarea de Cowork"
        : r.not_before && new Date(r.not_before) > new Date()
          ? `En cola: sin cupo en los motores gratis, sigue el ${fecha(r.not_before)} ${new Date(r.not_before).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}`
          : "En cola: empieza en menos de un minuto"
      : r.status === "investigando"
        ? "Investigando… (tarda de 30 segundos a 2 minutos)"
        : r.status === "error"
          ? `No se pudo investigar: ${r.error ?? "error desconocido"}`
          : null;

/** Cuántas consultas gasta investigar n empresas con ese motor (aviso antes de gastar). */
export const costNote = (n: number, engine: EngineId, st?: EngineStatus[]) => {
  if (engine === "claude_plan")
    return `${n} empresa${n === 1 ? "" : "s"} entra${n === 1 ? "" : "n"} a la cola de Claude con tu plan (no gasta cupo gratis; usa tu plan cuando corra la tarea de Cowork).`;
  const tav = st?.find((e) => e.service === "tavily");
  const ai = `unas ${n * 2} consultas de IA (2 por empresa)`;
  const search =
    engine === "gemini"
      ? ""
      : ` y hasta ${n * 3} búsquedas de Tavily (3 por empresa; si usa Gemini, busca en Google sin gastar Tavily)`;
  const left = tav?.blocked
    ? " Tavily ya llegó al 90 % del mes: solo se leerá el sitio web."
    : tav?.month_limit
      ? ` A Tavily le quedan ${(tav.month_limit - tav.month_used).toLocaleString("es-MX")} búsquedas este mes.`
      : "";
  const paid =
    engine === "deepseek" || engine === "claude_api"
      ? ` Este motor es de pago: cuesta aprox. ${engine === "deepseek" ? "$0.01" : "$0.30"} USD por empresa.`
      : "";
  return `Vas a gastar ${ai}${search}.${left}${paid}`;
};

/** Botón "Investigar con IA" + selector de motor (ficha y selección masiva). */
export const ResearchButton = ({
  ids,
  resolveIds,
  label = "Investigar con IA",
  onDone,
  size = "sm",
  disabled,
}: {
  ids?: number[];
  resolveIds?: () => Promise<number[]>; // empresas sin fila en el directorio
  label?: string;
  onDone?: () => void;
  size?: "sm" | "default";
  disabled?: boolean;
}) => {
  const { data: cfg } = useAiSettings();
  const { data: st } = useEngines();
  const [engine, setEngine] = useState<EngineId | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const notify = useNotify();
  const qc = useQueryClient();
  const e = engine ?? cfg?.ai_engine ?? "auto";
  const n = ids?.length ?? 1;
  const why = engineReady(e, st);
  const run = async () => {
    setBusy(true);
    try {
      const list = resolveIds ? await resolveIds() : (ids ?? []);
      const { data, error } = await getSupabaseClient().rpc(
        "research_enqueue",
        { p_ids: list, p_engine: e },
      );
      if (error) throw new Error(error.message);
      notify(
        data
          ? `${data} en cola con ${engineShort(e)}${e === "claude_plan" ? ": Claude las investiga en la próxima tarea de Cowork" : ": empieza en menos de un minuto"}`
          : "Ya estaban en cola",
        { type: "success" },
      );
      qc.invalidateQueries({ queryKey: ["research"] });
      qc.invalidateQueries({ queryKey: ["ai_engines"] });
      setOpen(false);
      onDone?.();
    } catch (err) {
      notify((err as Error).message, { type: "error" });
    }
    setBusy(false);
  };
  return (
    <>
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <Button
          size={size}
          disabled={disabled || busy}
          onClick={() => setOpen(true)}
          className="bg-[var(--ai)] text-white hover:bg-[var(--ai)]/90"
        >
          <Sparkles /> {label}
        </Button>
        <EngineSelect value={e} onChange={setEngine} />
      </span>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Investigar {n === 1 ? "esta empresa" : `${n} empresas`} con{" "}
              {engineShort(e)}
            </DialogTitle>
            <DialogDescription>{costNote(n, e, st)}</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Lee su sitio web, busca noticias, vacantes, redes, perfiles
            públicos, SIGER y REPSE, revisa la lista 69-B del SAT y arma el
            match, la estrategia y el guion. No contacta a nadie ni la pasa al
            CRM.
          </p>
          {why && (
            <p className="text-sm font-semibold text-[var(--critical)]">
              {why}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={busy}
            >
              Cancelar
            </Button>
            <Button onClick={run} disabled={busy || !!why}>
              {busy ? "Enviando…" : "Investigar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/* ------------------------------------------------------------ textos por validar */
export type SalesText = {
  id: number;
  offer_slug: string;
  kind: string;
  idx: number;
  label: string | null;
  body: string;
  status: "pendiente" | "aprobado" | "descartado";
};
export const useSalesTexts = () =>
  useQuery({
    queryKey: ["sales_texts"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("sales_texts")
        .select("*")
        .order("offer_slug")
        .order("kind")
        .order("idx");
      if (error) throw error;
      return data as SalesText[];
    },
  });

/** Marca "pendiente" junto a un texto base que Ian aún no aprueba. */
export const PendingMark = ({ status }: { status?: string }) =>
  status === "pendiente" ? (
    <span
      className="ml-1.5 inline-block rounded-full border border-[var(--warning,#d4a017)] px-1.5 py-px align-middle text-[10px] font-bold text-[var(--warning-ink,#8a6500)]"
      title="Texto base pendiente de validar en IA y ajustes → Textos por validar"
    >
      pendiente
    </span>
  ) : null;
